import { useState } from 'react';
import { AlertCircle, Calendar, CheckSquare, Clock, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { formatDate } from '../utils/formatDate';
import { todoUrgency, type Todo, type UseTodosResult } from '../hooks/useTodos';

/**
 * TodoList — a person's own to-dos as a Queue section: add one, tick one,
 * delete one. The list and its actions come from useTodos, so the Queue's
 * count and this section can never disagree.
 */

interface TodoListProps {
  todos: UseTodosResult;
}

function dueLabel(dueDate: string): string {
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return '';
  const days = Math.ceil((due.getTime() - Date.now()) / 86_400_000);
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`;
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days <= 7) return `In ${days} days`;
  return formatDate(dueDate, { month: 'short', day: 'numeric' }, 'en-US');
}

function TodoRow({ todo, onToggle, onDelete }: { todo: Todo; onToggle: (completed: boolean) => void; onDelete: () => void }) {
  const urgency = todoUrgency(todo);
  const tone = todo.completed ? 'hover:bg-gray-50' : urgency === 'overdue' ? 'bg-red-50 hover:bg-red-100' : urgency === 'dueSoon' ? 'bg-yellow-50 hover:bg-yellow-100' : 'hover:bg-gray-50';
  return (
    <div className={`group flex items-start gap-2 rounded p-2 transition-colors ${tone}`}>
      <Checkbox checked={todo.completed} onCheckedChange={(checked) => onToggle(!!checked)} className="mt-0.5" aria-label={todo.completed ? 'Mark as not done' : 'Mark as done'} />
      <div className="min-w-0 flex-1">
        <span className={`block break-words ${todo.completed ? 'text-muted-foreground line-through' : ''}`}>{todo.text}</span>
        {todo.dueDate && (
          <span className={`mt-1 flex items-center gap-1 ${todo.completed ? 'text-gray-400 line-through' : urgency === 'overdue' ? 'text-red-600' : urgency === 'dueSoon' ? 'text-yellow-700' : 'text-gray-500'}`}>
            {urgency === 'overdue' && !todo.completed ? <AlertCircle className="size-3" aria-hidden="true" /> : urgency === 'dueSoon' && !todo.completed ? <Clock className="size-3" aria-hidden="true" /> : <Calendar className="size-3" aria-hidden="true" />}
            {dueLabel(todo.dueDate)}
          </span>
        )}
      </div>
      <Button variant="ghost" size="sm" onClick={onDelete} className="h-6 w-6 shrink-0 p-0 opacity-0 group-hover:opacity-100" aria-label="Delete to-do">
        <Trash2 className="size-3 text-gray-400" aria-hidden="true" />
      </Button>
    </div>
  );
}

export function TodoList({ todos }: TodoListProps) {
  const [text, setText] = useState('');
  const [dueDate, setDueDate] = useState('');
  const { pending, completed, loading, addTodo, toggleTodo, deleteTodo } = todos;
  const overdue = pending.filter((t) => todoUrgency(t) === 'overdue').length;

  const submit = async () => {
    if (await addTodo(text, dueDate)) { setText(''); setDueDate(''); }
  };

  return (
    <Card data-testid="queue-todos">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <CheckSquare className="size-4" aria-hidden="true" />
          My to-dos
          {pending.length > 0 && <Badge variant="secondary">{pending.length}</Badge>}
          {overdue > 0 && <Badge variant="destructive">{overdue} overdue</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-2 md:flex-row">
          <Input
            placeholder="Add a to-do…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submit(); } }}
            className="flex-1"
            aria-label="New to-do"
          />
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="md:w-44" aria-label="Due date (optional)" />
          <Button size="sm" onClick={submit} disabled={!text.trim()}>
            <Plus className="mr-1 size-4" aria-hidden="true" />Add
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="size-5 animate-spin text-gray-400" aria-hidden="true" /></div>
        ) : pending.length === 0 && completed.length === 0 ? (
          <p className="text-muted-foreground">No to-dos. Add one above, or leave this empty: the sections above already hold what needs you.</p>
        ) : (
          <>
            {pending.length > 0 && (
              <div className="space-y-1">
                {pending.map((todo) => (
                  <TodoRow key={todo.id} todo={todo} onToggle={(c) => void toggleTodo(todo.id, c)} onDelete={() => void deleteTodo(todo.id)} />
                ))}
              </div>
            )}
            {completed.length > 0 && (
              <details className="rounded border bg-gray-50/50 p-2">
                <summary className="cursor-pointer text-muted-foreground">{completed.length} done</summary>
                <div className="mt-2 space-y-1">
                  {completed.map((todo) => (
                    <TodoRow key={todo.id} todo={todo} onToggle={(c) => void toggleTodo(todo.id, c)} onDelete={() => void deleteTodo(todo.id)} />
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
